import { useState } from 'react'
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import { cn } from '@/lib/utils'
import type { TimeseriesPoint } from '@/api/metrics'

interface MetricsChartProps {
  data: TimeseriesPoint[]
  loading?: boolean
}

type MetricKey = 'spend' | 'sales' | 'acos' | 'roas'

const metricConfig: Record<MetricKey, { label: string; color: string; format: (v: number) => string; yAxisId: string }> = {
  spend: { label: 'Spend', color: '#3b82f6', format: v => `${v.toFixed(2)} EUR`, yAxisId: 'left' },
  sales: { label: 'Sales', color: '#10b981', format: v => `${v.toFixed(2)} EUR`, yAxisId: 'left' },
  acos: { label: 'ACoS', color: '#ef4444', format: v => `${v.toFixed(1)}%`, yAxisId: 'right' },
  roas: { label: 'ROAS', color: '#8b5cf6', format: v => `${v.toFixed(2)}x`, yAxisId: 'right' },
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`
}

export default function MetricsChart({ data, loading = false }: MetricsChartProps) {
  const [activeMetrics, setActiveMetrics] = useState<Set<MetricKey>>(new Set(['spend', 'acos']))

  function toggleMetric(key: MetricKey) {
    setActiveMetrics(prev => {
      const next = new Set(prev)
      if (next.has(key)) { if (next.size > 1) next.delete(key) }
      else next.add(key)
      return next
    })
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
        <div className="h-[300px] flex items-center justify-center text-sm text-[var(--muted-foreground)]">Lade Chart...</div>
      </div>
    )
  }

  const hasLeftAxis = activeMetrics.has('spend') || activeMetrics.has('sales')
  const hasRightAxis = activeMetrics.has('acos') || activeMetrics.has('roas')

  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
      {/* Toggle Buttons */}
      <div className="mb-4 flex gap-2">
        {(Object.keys(metricConfig) as MetricKey[]).map(key => (
          <button
            key={key}
            onClick={() => toggleMetric(key)}
            className={cn(
              'rounded-md px-3 py-1 text-xs font-medium transition-colors border',
              activeMetrics.has(key)
                ? 'border-transparent text-white'
                : 'border-[var(--border)] bg-[var(--background)] text-[var(--muted-foreground)] hover:bg-[var(--accent)]',
            )}
            style={activeMetrics.has(key) ? { backgroundColor: metricConfig[key].color } : undefined}
          >
            {metricConfig[key].label}
          </button>
        ))}
      </div>

      {/* Chart */}
      {data.length === 0 ? (
        <div className="h-[300px] flex items-center justify-center text-sm text-[var(--muted-foreground)]">Keine Daten fuer diesen Zeitraum</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
            <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
            {hasLeftAxis && (
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)"
                tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} />
            )}
            {hasRightAxis && (
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)"
                tickFormatter={(v: number) => `${v}%`} />
            )}
            <Tooltip
              contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }}
              labelFormatter={formatDate}
              formatter={(value: number, name: string) => {
                const config = metricConfig[name as MetricKey]
                return config ? [config.format(value), config.label] : [value, name]
              }}
            />
            {activeMetrics.has('spend') && (
              <Bar dataKey="spend" yAxisId="left" fill="#3b82f6" opacity={0.3} radius={[2, 2, 0, 0]} />
            )}
            {activeMetrics.has('sales') && (
              <Bar dataKey="sales" yAxisId="left" fill="#10b981" opacity={0.3} radius={[2, 2, 0, 0]} />
            )}
            {activeMetrics.has('acos') && (
              <Line type="monotone" dataKey="acos" yAxisId={hasRightAxis ? 'right' : 'left'} stroke="#ef4444" strokeWidth={2} dot={false} />
            )}
            {activeMetrics.has('roas') && (
              <Line type="monotone" dataKey="roas" yAxisId={hasRightAxis ? 'right' : 'left'} stroke="#8b5cf6" strokeWidth={2} dot={false} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
